// A project's agent configurations, slot by slot as on the team settings page: the slots
// it has, then those it can take (docs/Y2026-Q4/design-agent-configs.md sections 5 and 11).
// Hidden where default is the only config there is, as in production today.
import { DEFAULT_SLOT } from "../api/agentSlot";
import {
  type AgentSlot,
  type AvailableConfig,
  type RunAllResult,
  type SlotStart,
  useAgentConfigs,
  useEnableAgentConfig,
  useProjectAgentConfigs,
  useRemoveSlot,
  useRunEveryConfig,
  useSetSlotStatus,
} from "../api/hooks/useAgentConfigs";
import {
  slotAvailable,
  slotKey,
  slotKeyName,
  slotName,
  slotOrigin,
  slotStatusText,
} from "../utils/agentSlotText";
import styles from "./AgentConfigsCard.module.css";
import { SettingSubcard } from "./SettingSubcard";
import { SettingsGroup } from "./SettingsGroup";
import { SECTION_TITLES } from "./settingsSections";

const RUN_STATUS: Record<string, string> = {
  started: "started",
  queued: "queued",
  not_enrolled: "nothing enrolled to run",
};

export const AgentConfigsCard = ({ projectId }: { projectId: string }) => {
  const query = useProjectAgentConfigs(projectId);
  const configs = useAgentConfigs().data?.configs;
  const runAll = useRunEveryConfig(projectId);
  if (query.isLoading) return null;
  if (query.isError || !query.data) {
    return (
      <SettingsGroup title={SECTION_TITLES.configs}>
        <p className={styles.error}>
          Could not load this project&apos;s configurations.{" "}
          {query.error instanceof Error ? query.error.message : ""}
        </p>
      </SettingsGroup>
    );
  }
  const { slots, available } = query.data;
  const shadows = slots.filter((s) => s.slot !== DEFAULT_SLOT);
  if (shadows.length === 0 && available.length === 0) return null;
  const anyActive = shadows.some((s) => s.status === "active" && slotAvailable(s));
  const costNote = (configKey: string) => configs?.find((c) => c.key === configKey)?.cost_note;
  const runResults = runAll.data?.results ?? runAll.data?.queued;

  return (
    <SettingsGroup title={SECTION_TITLES.configs}>
      {shadows.length > 0 && (
        <div className={styles.runAll}>
          <button
            type="button"
            className={styles.button}
            disabled={!anyActive || runAll.isLoading}
            onClick={() => runAll.mutate({})}
          >
            {runAll.isLoading ? "Queuing…" : "Run every config now"}
          </button>
          {runAll.isError && (
            <p className={styles.error}>Could not queue the runs: {runAll.error.message}</p>
          )}
          {runResults && (
            <RunAllResults
              results={runResults}
              name={(key) => {
                const slot = slots.find((s) => s.slot === key);
                return slot ? slotName(slot) : slotKeyName(key, configs);
              }}
            />
          )}
        </div>
      )}
      {shadows.map((slot) => (
        <SlotSubcard
          key={slot.slot}
          projectId={projectId}
          slot={slot}
          description={configs?.find((c) => c.key === slot.config_key)?.description ?? ""}
          costNote={costNote(slot.config_key)}
        />
      ))}
      {available.flatMap((config) =>
        config.starts.map((start) => (
          <AvailableSubcard
            key={slotKey(config.key, start)}
            projectId={projectId}
            config={config}
            start={start}
            costNote={config.cost_note ?? costNote(config.key)}
          />
        )),
      )}
    </SettingsGroup>
  );
};

const RunAllResults = ({
  results,
  name,
}: {
  results: RunAllResult[];
  name: (slot: string) => string;
}) =>
  results.length === 0 ? (
    <p className={styles.note}>Nothing to run.</p>
  ) : (
    <ul className={styles.results}>
      {results.map((r) => (
        <li key={r.slot} className={r.error ? styles.error : styles.note}>
          {name(r.slot)}: {r.error ?? RUN_STATUS[r.status ?? ""] ?? r.status ?? "queued"}
        </li>
      ))}
    </ul>
  );

const SlotSubcard = ({
  projectId,
  slot,
  description,
  costNote,
}: {
  projectId: string;
  slot: AgentSlot;
  description: string;
  costNote: string | undefined;
}) => {
  const setStatus = useSetSlotStatus(projectId);
  const remove = useRemoveSlot(projectId);
  const available = slotAvailable(slot);
  const paused = available && slot.status === "paused";
  const busy = setStatus.isLoading || remove.isLoading;
  const name = slotName(slot);
  const status = slotStatusText(
    available ? slot.status : "unavailable",
    slot.paused_reason,
    slot.unavailable_reason,
  );
  const confirmRemove = () => {
    if (
      window.confirm(
        `Remove ${name} from this project? Everything it has written here is deleted.`,
      )
    ) {
      remove.mutate({ slot: slot.slot });
    }
  };
  return (
    <SettingSubcard
      title={name}
      describe={description}
      notes={costNote ? [costNote] : []}
    >
      <p className={available && !paused ? styles.note : styles.paused}>
        {status}, {slotOrigin(slot)}
      </p>
      <span className={styles.actions}>
        {available && (
          <button
            type="button"
            className={styles.button}
            disabled={busy}
            onClick={() =>
              setStatus.mutate({ slot: slot.slot, status: paused ? "active" : "paused" })
            }
          >
            {paused ? "Resume" : "Pause"}
          </button>
        )}
        <button
          type="button"
          className={`${styles.button} ${styles.danger}`}
          disabled={busy}
          onClick={confirmRemove}
        >
          {remove.isLoading ? "Removing…" : "Remove"}
        </button>
      </span>
      {setStatus.isError && (
        <p className={styles.error}>Could not change it: {setStatus.error.message}</p>
      )}
      {remove.isError && (
        <p className={styles.error}>Could not remove it: {remove.error.message}</p>
      )}
    </SettingSubcard>
  );
};

const AvailableSubcard = ({
  projectId,
  config,
  start,
  costNote,
}: {
  projectId: string;
  config: AvailableConfig;
  start: SlotStart;
  costNote: string | undefined;
}) => {
  const enable = useEnableAgentConfig(projectId);
  return (
    <SettingSubcard
      title={slotName({ label: config.label, start })}
      describe={config.description}
      notes={costNote ? [costNote] : []}
    >
      <span className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          disabled={enable.isLoading}
          onClick={() => enable.mutate({ config_key: config.key, start })}
        >
          {enable.isLoading ? "Switching on…" : "Switch on"}
        </button>
      </span>
      {enable.isError && (
        <p className={styles.error}>Could not switch it on: {enable.error.message}</p>
      )}
    </SettingSubcard>
  );
};
